import { useEffect, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import CreatableSelect from 'react-select/creatable';
import { fetchDataAndUpdateStore, fetchTagsAndAppendToModule } from '../../redux/actions';
import apiServices from '../../services/service';
import { useDispatch, useSelector } from 'react-redux';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import { toast } from 'react-toastify';
import { getTagsByModule } from '../../redux/selector';
import { useLocation } from 'react-router-dom';
import { contactInputData, leadInputData } from '../../shared/constants';

const Tags = ({ localData, module, storeKey, handleClose, removeMode, selectedRows }) => {
  const stateData = useSelector((state) => state);
  let storeLoading = isLoadingByKey(stateData, 'tagsList');
  const tagsList = localData?.tags;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(storeLoading);
  const [formData, setFormData] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const location = useLocation();
  const tagsInput = { module: [module] };
  const [initialTags, setInitialTags] = useState([]);
  const [showPopover, setShowPopover] = useState(false);
  const [moduleSelector, setModuleSelector] = useState();
  const handleCancel = () => {
    // Simply close the popover
    setShowPopover(false);
  };

  // Load initial tags when component loads
  useEffect(() => {
    setInitialTags(tagsList); // Set initial state when tags are first loaded
  }, [selectedTags]);

  const moduleMap = {
    Lead: 'leads',
    Contact: 'contactList',
    Account: 'accountList',
    Opportunity: 'opportunityList',
    Campaign: 'campaignList',
    Ticket: 'ticketList',
  };

  useEffect(() => {
    setModuleSelector(moduleMap[module] || ''); // Default to empty string if module is not found
  }, [module]);

  const tagsByModule = useSelector(getTagsByModule(moduleSelector));

  const excludedTags =
    tagsByModule?.filter(
      (moduleTag) => !tagsList?.some((localTag) => localTag.id === moduleTag.id)
    ) || [];

  useEffect(() => {
    dispatch(fetchDataAndUpdateStore({ key: 'tags', inputData: tagsInput }));
  }, []);

  useEffect(() => {
    setSelectedTags(tagsList?.map((u) => ({ value: u.id, label: u.name })));
  }, [tagsList]);

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  const handleSelectChange = (selectedOptions) => {
    // Update selected tags state
    setSelectedTags(selectedOptions);

    // Separate existing options and newly created ones
    const tagNames = selectedOptions.map((option) => option.label).flat();

    // Update formData with the required payload structure
    setFormData((prevFormData) => ({
      ...prevFormData,
      tagName: tagNames, // Array of tag names (existing + newly created)
    }));
  };

  const handleMultiSubmit = async (formData) => {
    setLoading(true);
    if (removeMode === true) {
      const updatedTags = selectedTags.map((row) => row.value);
      const selectedIds = selectedRows.map((row) => row.id);
      const data = {
        id: updatedTags,
        module: module,
        moduleId: selectedIds,
      };
      await apiServices
        .deleteTagsByModuleId(data)
        .then((response) => {
          if (response.status === 200) {
            setLoading(false);
            handleClose();
            toast.success('Tags Deleted Successfully!', {
              position: 'top-right',
            });
          }
          dispatch(
            fetchDataAndUpdateStore({
              key: moduleSelector,
              inputData: module === 'Lead' ? leadInputData : contactInputData,
            })
          );
          dispatch(
            fetchDataAndUpdateStore({
              key: 'tagsByModule',
              inputData: module,
              storeKey: storeKey,
            })
          );
        })
        .catch((error) => {
          setLoading(false);
          console.error('Error deleting tags:', error);
          toast.error('Failed to delete tags.', { position: 'top-right' });
        });
    } else {
      const updatedTags = selectedTags ? selectedTags.map((row) => row.label) : [formData.tagName];
      const selectedIds = selectedRows ? selectedRows.map((row) => row.id) : [''];
      const data = {
        tagName: updatedTags,
        module: module,
        moduleId: selectedIds,
      };
      await apiServices
        .createTags(data)
        .then((response) => {
          if (response.status === 200) {
            handleClose();
            toast.success('Tags Added Successfully!', {
              position: 'top-right',
            });
            dispatch(
              fetchDataAndUpdateStore({
                key: moduleSelector,
                inputData: module === 'Lead' ? leadInputData : contactInputData,
              })
            );
            dispatch(
              fetchDataAndUpdateStore({
                key: 'tagsByModule',
                inputData: module,
                storeKey: storeKey,
              })
            );
            setLoading(false);
          }
        })
        .catch((error) => {
          setLoading(false);
          console.error('Error adding tags:', error);
          toast.error('Failed to add tags.', { position: 'top-right' });
        });
    }
  };

  const handleSubmit = async (formData) => {
    setLoading(true);

    // Identify removed and added tags
    const removedTags = initialTags.filter(
      (initialTag) => !selectedTags.some((tag) => tag.value === initialTag.id)
    );
    const addedTags = selectedTags.filter(
      (selectedTag) => !initialTags.some((tag) => tag.id === selectedTag.value)
    );

    // Handle tag deletion
    if (removedTags.length > 0) {
      const removedTagIds = removedTags.map((tag) => tag.id); //For an array of Ids
      // const removedTagIds = removedTags.map((tag) => tag.id).join(","); //For string
      const data = {
        id: removedTagIds,
        module: module,
        moduleId: [localData.id],
      };

      await apiServices
        .deleteTagsByModuleId(data)
        .then((response) => {
          if (response.status === 200) {
            setLoading(false);
            setShowPopover(false);
            toast.success('Tags Deleted Successfully!', {
              position: 'top-right',
            });
          }
        })
        .catch((error) => {
          setLoading(false);
          console.error('Error deleting tags:', error);
          toast.error('Failed to delete tags.', { position: 'top-right' });
        });
      dispatch(
        fetchTagsAndAppendToModule({
          moduleName: module,
          id: localData?.id,
        })
      );
      dispatch(
        fetchDataAndUpdateStore({
          key: 'tagsByModule',
          inputData: module,
          storeKey: storeKey,
        })
      );
    }

    // Handle tag creation
    if (addedTags.length > 0) {
      const tagNames = addedTags.map((tag) => tag.label);

      const updatedFormData = {
        ...formData,
        moduleId: [localData?.id],
        module,
        tagName: tagNames, // Send the added tag names
      };

      await apiServices
        .createTags(updatedFormData)
        .then((response) => {
          if (response.status === 200) {
            setShowPopover(false);
            toast.success('Tags Added Successfully!', {
              position: 'top-right',
            });
            dispatch(
              fetchDataAndUpdateStore({
                key: 'tagsByModule',
                inputData: module,
                storeKey: storeKey,
              })
            );
            dispatch(
              fetchTagsAndAppendToModule({
                moduleName: module,
                id: localData?.id,
              })
            );
          }
        })
        .catch((error) => {
          console.error('Error adding tags:', error);
          toast.error('Failed to add tags.', { position: 'top-right' });
        });
    }

    setLoading(false);
    setFormData([]);
  };

  return (
    <>
      {location.pathname.includes('view') ? (
        <OverlayTrigger
          trigger="click"
          placement="bottom"
          rootClose={true}
          show={showPopover}
          onToggle={() => setShowPopover(!showPopover)}
          overlay={
            <Popover id="popover-basic" className="tagPopover">
              <Popover.Body>
                <CreatableSelect
                  name={'tagIds'}
                  isMulti
                  placeholder={'Enter Tags'}
                  styles={{
                    container: (base) => ({
                      ...base,
                      width: '100%',
                    }),
                    control: (base) => ({
                      ...base,
                      width: '600px',
                    }),
                  }}
                  value={selectedTags} // Replace with your state for selected tags
                  onChange={handleSelectChange}
                  options={excludedTags?.map((data) => ({
                    value: data.id, // Use `id` for the value
                    label: data.name.join(', '), // Convert the `name` array into a comma-separated string
                  }))}
                  // createOptionPosition="first" // Newly created options appear at the top
                />
                <div
                  className="tagBtn spacing_bt"
                  style={{
                    marginTop: '20px',
                    display: 'flex',
                    justifyContent: 'end',
                    gap: '10px',
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    data-bs-dismiss="modal"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      handleSubmit(formData);
                    }}
                    disabled={loading}
                  >
                    {loading && (
                      <>
                        {' '}
                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                      </>
                    )}
                    {loading ? 'Loading...' : 'Save'}
                  </button>
                </div>
              </Popover.Body>
            </Popover>
          }
        >
          <div
            className="flex-div tag"
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            onClick={() => setShowPopover(!showPopover)}
          >
            <img src="/assets/images/tag.svg" alt="" title="" className="me-2"></img>

            <small id="viewtags">
              <div className="">
                {tagsList && tagsList.length > 0 ? (
                  tagsList.map((data, index) => (
                    <span key={index} className="tag_arrow arw-right color-tag">
                      {data.name}
                    </span>
                  ))
                ) : (
                  <span className="placeholder">Add Tags</span>
                )}
              </div>
            </small>
          </div>
        </OverlayTrigger>
      ) : (
        <>
          <div className="content-area">
            <CreatableSelect
              name={'tagIds'}
              isMulti
              placeholder={'Enter Tags'}
              styles={{
                container: (base) => ({
                  ...base,
                  width: '100%',
                }),
                control: (base) => ({
                  ...base,
                  width: '600px',
                }),
              }}
              value={selectedTags} // Replace with your state for selected tags
              onChange={handleSelectChange}
              options={excludedTags?.map((data) => ({
                value: data.id, // Use `id` for the value
                label: data.name.join(', '), // Convert the `name` array into a comma-separated string
              }))}
              // createOptionPosition="first" // Newly created options appear at the top
            />
          </div>
          <div className="popup-footer">
            <button
              type="button"
              className="btn btn-outline-primary"
              data-bs-dismiss="modal"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                handleMultiSubmit(formData);
              }}
              disabled={loading}
            >
              Save
            </button>
          </div>
        </>
      )}
    </>
  );
};

export default Tags;
